import { inject, Injectable } from '@angular/core';
import {
    AsnHeaderDto,
    AsnHeaderFacade,
    AsnLineDto,
    ExceptionFacade,
    ReceiptHeaderFacade,
    ReceiptLineDto,
} from '@pf/platform-wms/domain';
import {
    combineLatest,
    debounceTime,
    first,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
} from 'rxjs';
import {
    PurchaseOrderHeaderDto,
    PurchaseOrderHeaderFacade,
} from '@pf/platform-orders/domain';
import {
    KitComponentSummary,
    PoLineAsnShipment,
    PoLineReceipt,
    PoLineStatus,
    PoLineSummary,
} from '../models/po-line-summary.model';
import {
    isPackSizeUomKit,
    parseComponentIdFromKitCustomFieldKey,
    parseKitBomQuantity,
    partitionKitBomCustomFields,
} from '@pf/platform-item-catalog/domain';
import {
    AsnStatus,
    PurchaseOrderStatus,
    ReceiptStatus,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';
import { PurchaseOrderLineViewModelRead } from '@control-tower/platform-orders';
import { ICustomFieldDto } from '@pf/shared/util-platform';

type EnrichedReceiptLine = ReceiptLineDto & {
    _asnHeaderId: string | null;
    _headerIsReceived: boolean;
    _hasShortException?: boolean;
};

@Injectable()
export class PoLookupService {
    private readonly receiptHeaderFacade = inject(ReceiptHeaderFacade);
    private readonly purchaseOrderHeaderFacade = inject(
        PurchaseOrderHeaderFacade
    );
    private readonly asnHeaderFacade = inject(AsnHeaderFacade);
    private readonly exceptionFacade = inject(ExceptionFacade);

    lookupByPoOrAsnTrackingNumber$(
        lookupValue: string
    ): Observable<PurchaseOrderHeaderDto | null> {
        return this.purchaseOrderHeaderFacade
            .getByOrderNumber$(lookupValue)
            .pipe(
                switchMap(purchaseOrder => {
                    if (!purchaseOrder) {
                        return this.asnHeaderFacade
                            .getByTrackingNumber$(lookupValue)
                            .pipe(
                                switchMap(asn => {
                                    if (!asn) {
                                        return of(null);
                                    }

                                    const poNumber = asn.externalReferenceId
                                        ?.split('-')
                                        .slice(0, -2)
                                        .join('-');
                                    if (!poNumber) {
                                        return of(null);
                                    }
                                    return this.purchaseOrderHeaderFacade.getByOrderNumber$(
                                        poNumber
                                    );
                                })
                            );
                    }

                    return of(purchaseOrder);
                })
            );
    }

    getPoLines$(
        purchaseOrder: PurchaseOrderHeaderDto
    ): Observable<PoLineSummary[]> {
        return this.loadLineData(purchaseOrder);
    }

    private loadLineData(
        po: PurchaseOrderHeaderDto
    ): Observable<PoLineSummary[]> {
        const poLines = po.purchaseOrderLines ?? [];
        if (poLines.length === 0) {
            return of([]);
        }

        const poLineIds = poLines.map(l => l.id).filter(Boolean) as string[];

        return combineLatest([
            this.asnHeaderFacade.getByPurchaseOrderId$(po.id),
            this.loadReceiptLines$(po.orderNumber),
        ]).pipe(
            map(([asnHeaders, receiptLines]) => {
                const allAsnLines = asnHeaders.flatMap(h => h.lines ?? []);
                const relevantReceipts = this.filterReceiptsByPoLines(
                    receiptLines,
                    poLineIds
                );

                return poLines.map((poLine, index) => {
                    const lineId = poLine.id as string;
                    const lineAsnLines = allAsnLines.filter(
                        al => al.purchaseOrderLineId === lineId
                    );
                    const lineReceipts = relevantReceipts.filter(
                        r => r.purchaseOrderLine?.id === lineId
                    );

                    const orderedQuantity = poLine.quantity ?? 0;
                    const receivedQuantity = lineReceipts.reduce(
                        (sum, r) => sum + (r.actualQuantity ?? 0),
                        0
                    );
                    const receivedPercentage =
                        orderedQuantity > 0
                            ? Math.round(
                                  (receivedQuantity / orderedQuantity) * 100
                              )
                            : 0;

                    const asnShipments = this.buildAsnShipments(
                        lineAsnLines,
                        asnHeaders
                    );

                    const receipts = this.buildReceiptLines(lineReceipts);

                    const status = this.computeLineStatus(
                        poLine.status,
                        receivedQuantity,
                        orderedQuantity,
                        receipts
                    );

                    const isReceivable = this.computeIsReceivable(
                        poLine,
                        receipts,
                        asnShipments,
                        status.status
                    );

                    const isKit = isPackSizeUomKit(poLine.item?.packSizeUom);

                    const kitComponents = isKit
                        ? this.buildKitComponents(poLine)
                        : [];

                    return {
                        id: lineId,
                        itemId: poLine.itemId ?? poLine.item?.id ?? '',
                        purchaseOrderHeaderId: po.id,
                        vendorLPN: po.externalReferenceId ?? 'unknown',
                        lineNumber: index + 1,
                        itemName: poLine.item?.name ?? 'Unknown Item',
                        itemSku: poLine.item?.code ?? '',
                        supplierName: po.vendor?.vendorName ?? '',
                        manufacturerName: poLine.item?.manufacturer?.name ?? '',
                        orderedQuantity,
                        receivedQuantity,
                        receivedPercentage,
                        status: status.status,
                        statusLabel: status.label,
                        isReceivable: isReceivable,
                        asnShipments,
                        receipts,
                        isKit,
                        kitComponents,
                    } as PoLineSummary;
                });
            }),
            debounceTime(300)
        );
    }

    private loadReceiptLines$(poNumber: string) {
        return this.receiptHeaderFacade.getByPoNumber$(poNumber).pipe(
            switchMap(headers => {
                const receiptLines = this.buildEnrichedReceiptLines(headers);
                if (receiptLines.length === 0) {
                    return of([] as EnrichedReceiptLine[]);
                }

                return forkJoin(
                    receiptLines.map(line =>
                        this.enrichReceiptLineWithShortException$(line)
                    )
                );
            })
        );
    }

    private buildEnrichedReceiptLines(
        headers: {
            receiptLines?: ReceiptLineDto[] | null;
            asnHeaderId?: string | null;
            status?: ReceiptStatus;
        }[]
    ): EnrichedReceiptLine[] {
        return headers.flatMap(h =>
            (h.receiptLines ?? []).map(line => ({
                ...line,
                _asnHeaderId: h.asnHeaderId ?? null,
                _headerIsReceived: h.status === ReceiptStatus.Received,
            }))
        );
    }

    private enrichReceiptLineWithShortException$(
        line: EnrichedReceiptLine
    ): Observable<EnrichedReceiptLine> {
        if (
            !line.id ||
            (line.actualQuantity ?? 0) >= (line.expectedQuantity ?? 0)
        ) {
            return of({
                ...line,
                _hasShortException: false,
            });
        }

        return this.getReceiptLineHasShortException$(line.id).pipe(
            map(hasShortException => ({
                ...line,
                _hasShortException: hasShortException,
            }))
        );
    }

    private filterReceiptsByPoLines(
        receiptLines: EnrichedReceiptLine[],
        poLineIds: string[]
    ): EnrichedReceiptLine[] {
        return receiptLines.filter(
            r =>
                r.purchaseOrderLine?.id &&
                poLineIds.includes(r.purchaseOrderLine.id)
        );
    }

    private buildAsnShipments(
        asnLines: AsnLineDto[],
        asnHeaders: AsnHeaderDto[]
    ): PoLineAsnShipment[] {
        return asnLines
            .map(line => {
                const header = asnHeaders.find(
                    h => h.id === (line.asnHeaderId as string)
                );
                if (!header) {
                    return null;
                }
                return {
                    asnHeaderId: line.asnHeaderId,
                    asnLineId: line.id,
                    shipmentNumber:
                        !line.externalReferenceId ||
                        (header.externalReferenceId &&
                            line.externalReferenceId.includes(
                                header.externalReferenceId
                            ))
                            ? ''
                            : line.externalReferenceId ?? '',
                    trackingNumber: header.trackingNumber ?? '',
                    carrierScac: header.carrier?.scac ?? '',
                    shippedQuantity: line.quantity ?? 0,
                    shipDate: header.shipDateTime ?? '',
                    status: line.status ?? header.status,
                    expectedDateTime: header.expectedDateTime ?? '',
                };
            })
            .filter(Boolean) as PoLineAsnShipment[];
    }

    private buildReceiptLines(
        receiptLines: EnrichedReceiptLine[]
    ): PoLineReceipt[] {
        return receiptLines.map(r => {
            const quantityShort =
                (r.actualQuantity ?? 0) < (r.expectedQuantity ?? 0);

            const quantityOver =
                (r.actualQuantity ?? 0) > (r.expectedQuantity ?? 0);

            const isReceived =
                r.status === ReceiptStatus.Received || r._headerIsReceived;
            const isPutaway =
                (isReceived && !r.receivedBundle) ||
                !!r.receivedBundle?.container?.slot;
            const isStagedForPutaway =
                isReceived &&
                !isPutaway &&
                !!r.receivedBundle?.container?.equipment;

            return {
                receiptHeaderId: r.inventoryReceiptId ?? '',
                receiptLineId: r.id ?? '',
                asnHeaderId: r._asnHeaderId,
                status: r.status,
                isStagedForPutaway,
                isPutaway,
                isReceived,
                dateReceived: r.createdDate ?? '',
                actualQuantity: r.actualQuantity ?? 0,
                expectedQuantity: r.expectedQuantity ?? 0,
                isShort: quantityShort && isReceived && r._hasShortException,
                isPartial: quantityShort && isReceived && !r._hasShortException,
                isOverage: quantityOver && isReceived,
                isExact: !quantityShort && !quantityOver && isReceived,
            } as PoLineReceipt;
        });
    }

    private getReceiptLineHasShortException$(receiptLineId: string) {
        return this.exceptionFacade
            .oneTimeSearch$({
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    sourceEntity: WmsPlatformEntity.ReceiptLine,
                    sourceEntityId: receiptLineId,
                },
            })
            .pipe(
                first(),
                map(result =>
                    result.data.some(
                        e => e.exceptionType?.code === 'receiving:short'
                    )
                )
            );
    }

    private computeLineStatus(
        poLineStatus: PurchaseOrderStatus | undefined,
        receivedQuantity: number,
        orderedQuantity: number,
        receipts: PoLineReceipt[]
    ): { status: PoLineStatus; label: string } {
        if (poLineStatus == PurchaseOrderStatus.Canceled) {
            return { status: 'canceled', label: 'Canceled' };
        }

        if (
            poLineStatus === PurchaseOrderStatus.Completed ||
            (receivedQuantity >= orderedQuantity && orderedQuantity > 0)
        ) {
            const allPutawayOrStaged =
                receipts.length > 0 &&
                receipts
                    .filter(r => r.isReceived)
                    .every(r => r.isPutaway || r.isStagedForPutaway);
            if (allPutawayOrStaged) {
                return { status: 'complete', label: 'Complete' };
            }
            return { status: 'received', label: 'Received' };
        }

        if (receivedQuantity > 0 && receivedQuantity < orderedQuantity) {
            const hasOverage = receipts.some(r => r.isOverage);
            if (hasOverage) {
                return { status: 'overage', label: 'Overage' };
            }
            const hasShort = receipts.some(r => r.isShort);
            if (hasShort) {
                return { status: 'short', label: 'Short' };
            }
            return { status: 'partial', label: 'Partial' };
        }

        return { status: 'not-started', label: 'Not Started' };
    }

    private computeIsReceivable(
        poLine: PurchaseOrderLineViewModelRead,
        receipts: PoLineReceipt[],
        asnShipments: PoLineAsnShipment[],
        status: PoLineStatus
    ): boolean {
        if (
            status === 'complete' ||
            status === 'received' ||
            status === 'canceled'
        ) {
            return false;
        }

        const hasNonAsnQuantity =
            poLine.quantity -
                asnShipments.reduce(
                    (sum, asn) => sum + (asn.shippedQuantity ?? 0),
                    0
                ) >
            0;
        const hasIncompleteAsns = asnShipments.some(
            asn =>
                asn.status !== AsnStatus.Completed &&
                asn.status !== AsnStatus.Canceled
        );
        const hasPartialReceipt = receipts
            .filter(r => r.isReceived)
            .some(r => r.isPartial);

        return hasNonAsnQuantity || hasIncompleteAsns || hasPartialReceipt;
    }

    private buildKitComponents(
        poLine: PurchaseOrderLineViewModelRead
    ): KitComponentSummary[] {
        const itemCustomFields = (
            poLine.item as Record<string, unknown> | undefined
        )?.['customFields'] as ICustomFieldDto[] | undefined;

        const { kitBom } = partitionKitBomCustomFields(itemCustomFields);

        return kitBom
            .map(cf => ({
                componentId:
                    parseComponentIdFromKitCustomFieldKey(cf.key) ?? '',
                perKitQuantity: parseKitBomQuantity(cf.value),
            }))
            .filter(
                (k): k is KitComponentSummary =>
                    k.componentId.length > 0 && k.perKitQuantity > 0
            );
    }
}
